import React, { useState } from 'react';
import { Bell, Calendar, FileText, DollarSign, Award, AlertCircle, CheckCircle, Clock, Filter, MailOpen, Mail } from 'lucide-react';

const notificationsData = [
  {
    id: 1,
    title: 'Assignment Due Tomorrow',
    message: 'Your Mathematics assignment "Quadratic Equations Problem Set" is due tomorrow. Make sure to submit it on time.',
    type: 'assignment',
    date: 'Jan 17, 2026',
    time: '09:00 AM',
    read: false,
    priority: 'high',
  },
  {
    id: 2,
    title: 'Mid-Term Exam Schedule Released',
    message: 'The mid-term examination schedule for February 2026 has been released. Please check the examination section for details.',
    type: 'exam',
    date: 'Jan 16, 2026',
    time: '02:30 PM',
    read: false,
    priority: 'medium',
  },
  {
    id: 3,
    title: 'Fee Payment Reminder',
    message: 'Your quarterly fee payment of $6,650 is due on January 31, 2026. Please make the payment to avoid late fees.',
    type: 'fee',
    date: 'Jan 15, 2026',
    time: '10:00 AM',
    read: true,
    priority: 'high',
  },
  {
    id: 4,
    title: 'Parent-Teacher Meeting',
    message: 'A parent-teacher meeting is scheduled for January 25, 2026, at 10:00 AM. Your parents are requested to attend.',
    type: 'event',
    date: 'Jan 14, 2026',
    time: '11:30 AM',
    read: true,
    priority: 'medium',
  },
  {
    id: 5,
    title: 'Result Published',
    message: 'Your Unit Test 2 results have been published. You scored 88% and secured 4th rank in class. Congratulations!',
    type: 'result',
    date: 'Jan 12, 2026',
    time: '04:00 PM',
    read: true,
    priority: 'low',
  },
  {
    id: 6,
    title: 'Library Book Return',
    message: 'Please return the library book "Advanced Physics" by January 20, 2026, to avoid penalty charges.',
    type: 'general',
    date: 'Jan 10, 2026',
    time: '09:15 AM',
    read: true,
    priority: 'low',
  },
  {
    id: 7,
    title: 'Annual Sports Day Registration',
    message: 'Registration for Annual Sports Day is now open. Last date to register is January 28, 2026.',
    type: 'event',
    date: 'Jan 8, 2026',
    time: '03:00 PM',
    read: true,
    priority: 'medium',
  },
  {
    id: 8,
    title: 'Holiday Notice',
    message: 'School will remain closed on January 26, 2026, on account of Republic Day.',
    type: 'general',
    date: 'Jan 5, 2026',
    time: '10:00 AM',
    read: true,
    priority: 'low',
  },
];

const filters = ['All', 'Unread', 'Assignment', 'Exam', 'Fee', 'Event', 'Result'];

const Notifications = () => {
  const [notifications, setNotifications] = useState(notificationsData);
  const [activeFilter, setActiveFilter] = useState('All');

  const filteredNotifications = notifications.filter((notification) => {
    if (activeFilter === 'All') return true;
    if (activeFilter === 'Unread') return !notification.read;
    return notification.type.toLowerCase() === activeFilter.toLowerCase();
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id) => {
    setNotifications(notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case 'assignment':
        return { icon: FileText, color: 'bg-blue-100 text-blue-600' };
      case 'exam':
        return { icon: Award, color: 'bg-purple-100 text-purple-600' };
      case 'fee':
        return { icon: DollarSign, color: 'bg-green-100 text-green-600' };
      case 'event':
        return { icon: Calendar, color: 'bg-orange-100 text-orange-600' };
      case 'result':
        return { icon: Award, color: 'bg-yellow-100 text-yellow-600' };
      default:
        return { icon: Bell, color: 'bg-gray-100 text-gray-600' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-600';
      case 'medium': return 'bg-yellow-100 text-yellow-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600 mt-1">Stay updated with school announcements and alerts</p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="flex items-center gap-2 px-4 py-2 text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
          >
            <CheckCircle size={18} />
            <span>Mark all as read</span>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Bell size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{notifications.length}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Mail size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{unreadCount}</p>
              <p className="text-sm text-gray-500">Unread</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertCircle size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">
                {notifications.filter(n => n.priority === 'high').length}
              </p>
              <p className="text-sm text-gray-500">High Priority</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <MailOpen size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {notifications.filter(n => n.read).length}
              </p>
              <p className="text-sm text-gray-500">Read</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <Filter size={18} className="text-gray-400 flex-shrink-0" />
        {filters.map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeFilter === filter
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {filter}
            {filter === 'Unread' && unreadCount > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {filteredNotifications.map((notification) => {
          const typeConfig = getTypeConfig(notification.type);
          const TypeIcon = typeConfig.icon;

          return (
            <div
              key={notification.id}
              onClick={() => markAsRead(notification.id)}
              className={`bg-white rounded-xl shadow-sm border overflow-hidden cursor-pointer transition-all hover:shadow-md ${
                notification.read ? 'border-gray-100' : 'border-primary-200 bg-primary-50/30'
              }`}
            >
              <div className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${typeConfig.color}`}>
                    <TypeIcon size={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`font-semibold ${notification.read ? 'text-gray-900' : 'text-primary-900'}`}>
                            {notification.title}
                          </h3>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-primary-600 rounded-full"></span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(notification.priority)}`}>
                            {notification.priority}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mt-1 line-clamp-2">{notification.message}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        <span>{notification.date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>{notification.time}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs capitalize">
                        {notification.type}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredNotifications.length === 0 && (
        <div className="text-center py-12">
          <Bell size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No notifications found for this filter.</p>
        </div>
      )}
    </div>
  );
};

export default Notifications;
